// src/realtime/presenceSocket.ts

import websocketManager from "../lib/websocketManager";
import type { AppDispatch } from "../redux/storeTypes";
import {
  setUserOnlineStatus,
  setUserLastSeen,
  setPresenceSnapshot,
} from "../redux/presence/presenceSlice";
import { logger } from "../utils/logging/logger";

// -----------------------------------------------------------------------------
// Register presence realtime event handlers
// -----------------------------------------------------------------------------
export const initializePresenceEvents = (dispatch: AppDispatch) => {
  const handlers: Record<string, (data: any) => void> = {};

  // Online / offline status
  handlers["user_online_status"] = (data) => {
    const userId = Number(data?.user_id);
    const isOnline = !!data?.is_online;

    if (!Number.isFinite(userId) || userId <= 0) return;

    dispatch(setUserOnlineStatus({ userId, isOnline }));
  };

  // Last seen
  handlers["user_last_seen"] = (data) => {
    const userId = Number(data?.user_id);
    if (!Number.isFinite(userId) || userId <= 0) return;

    dispatch(
      setUserLastSeen({
        userId,
        last_seen: data?.last_seen ?? null,
        last_seen_epoch:
          typeof data?.last_seen_epoch === "number"
            ? data.last_seen_epoch
            : null,
        last_seen_display: data?.last_seen_display ?? null,
      }),
    );
  };

  // Snapshot
  handlers["presence_snapshot"] = (data) => {
    const rawStatuses = data?.statuses;
    if (!rawStatuses || typeof rawStatuses !== "object") return;

    const normalized: Record<number, boolean> = {};

    for (const [rawUserId, rawValue] of Object.entries(rawStatuses)) {
      const userId = Number(rawUserId);
      if (!Number.isFinite(userId) || userId <= 0) continue;

      normalized[userId] = !!rawValue;
    }

    dispatch(setPresenceSnapshot(normalized));
  };

  // Optional canonical error event
  handlers["error"] = (data) => {
    logger.warn("[presenceSocket] presence error", data);
  };

  Object.entries(handlers).forEach(([eventType, callback]) => {
    websocketManager.on(`presence:${eventType}`, callback);
  });

  return () => {
    Object.entries(handlers).forEach(([eventType, callback]) => {
      websocketManager.off(`presence:${eventType}`, callback);
    });
  };
};

// -----------------------------------------------------------------------------
// Setup presence websocket
// -----------------------------------------------------------------------------
export const setupPresenceWebSocket =
  () => (dispatch: AppDispatch) => {
    const token = localStorage.getItem("access_token");

    if (!token) {
      logger.warn("[presenceSocket] No token; blocking websocket connection");
      return;
    }

    if (!websocketManager.isConnected()) {
      websocketManager.connect(token, "presence");
    }

    if (!websocketManager.hasEventsRegistered("presence")) {
      initializePresenceEvents(dispatch);
      websocketManager.markEventsAsRegistered("presence");
    }
  };

// -----------------------------------------------------------------------------
// Cleanup presence websocket
// -----------------------------------------------------------------------------
export const cleanupPresenceWebSocket = () => {
  websocketManager.clearRegisteredEvents("presence");
};

// -----------------------------------------------------------------------------
// Request snapshot for a set of users
// -----------------------------------------------------------------------------
export function requestPresenceSnapshot(userIds: number[]) {
  const normalized = Array.from(
    new Set(
      (userIds || [])
        .map((id) => Number(id))
        .filter((id) => Number.isFinite(id) && id > 0),
    ),
  );

  if (!normalized.length) return;
  if (!websocketManager.isConnected()) return;

  websocketManager.sendMessage("presence", {
    type: "request_presence_snapshot",
    data: {
      user_ids: normalized,
    },
  });
}